'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Product {
  id: string;
  name: string;
  image: string;
  alt: string;
  strength: string;
  dosageForm: string;
}

const ProductSlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const products: Product[] = [
    {
      id: 'prod-001',
      name: 'Pegaspargase Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_197992022-1765310132725.png",
      alt: 'Medical injection vial with clear solution for oncology treatment',
      strength: '750 IU/mL',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-002',
      name: 'Amphotericin B Liposomal for Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5fad654-1767866255485.png",
      alt: 'Lyophilized powder vial for antifungal injection therapy',
      strength: '50 mg/vial',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-003',
      name: 'Leuprolide Acetate Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_16ccaee86-1767866255041.png",
      alt: 'Pre-filled syringe with hormone therapy medication',
      strength: '3.75 mg',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-004',
      name: 'Leuprolide Acetate Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_13001820a-1767866252029.png",
      alt: 'Pre-filled syringe with hormone therapy medication 7.5mg strength',
      strength: '7.5 mg',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-005',
      name: 'Leuprolide Acetate for Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fcd4e9c6-1767866255104.png",
      alt: 'Lyophilized powder vial for hormone therapy injection 11.25mg',
      strength: '11.25 mg',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-006',
      name: 'Leuprolide Acetate for Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a97f83e5-1767866252703.png",
      alt: 'Lyophilized powder vial for hormone therapy injection 22.5mg',
      strength: '22.5 mg',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-007',
      name: 'Octreotide Acetate Long-Acting Release Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_186d35581-1767866254766.png",
      alt: 'Lyophilized powder vial for long-acting octreotide injection 20mg',
      strength: '20 mg/vial',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-008',
      name: 'Octreotide Acetate Long-Acting Release Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e69e1918-1767866253195.png",
      alt: 'Lyophilized powder vial for long-acting octreotide injection 30mg',
      strength: '30 mg/vial',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-009',
      name: 'Paclitaxel (Protein-Bound Particles) for Injectable Suspension',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d5d95253-1767866252434.png",
      alt: 'Lyophilized powder vial for paclitaxel protein-bound injectable suspension',
      strength: '100 mg/vial',
      dosageForm: 'Injectable Suspension'
    },
    {
      id: 'prod-010',
      name: 'Doxorubicin Hydrochloride Liposomal Injection',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_14041c90e-1765310134104.png",
      alt: 'Red liposomal injection vial for oncology chemotherapy treatment',
      strength: '2 mg/mL',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-011',
      name: 'Caspofungin Acetate for Injection BP',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0e1f230-1767866255032.png",
      alt: 'Lyophilized powder vial for antifungal caspofungin injection 50mg',
      strength: '50 mg',
      dosageForm: 'Injection'
    },
    {
      id: 'prod-012',
      name: 'Caspofungin Acetate for Injection BP',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_175f5bd99-1767866255014.png",
      alt: 'Lyophilized powder vial for antifungal caspofungin injection 70mg',
      strength: '70 mg',
      dosageForm: 'Injection'
    }
  ];

  const productsPerSlide = 3;
  const totalSlides = Math.ceil(products.length / productsPerSlide);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const getCurrentProducts = () => {
    const startIndex = currentSlide * productsPerSlide;
    return products.slice(startIndex, startIndex + productsPerSlide);
  };

  return (
    <section className="py-8 md:py-12 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-textPrimary mb-2">
            Featured Products
          </h2>
          <p className="text-sm md:text-base text-textSecondary max-w-2xl mx-auto">
            Explore our comprehensive range of pharmaceutical products
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="relative bg-card rounded-xl shadow-lg overflow-hidden border border-border">
            {/* Slideshow Container */}
            <div className="relative overflow-hidden">
              <div className="transition-opacity duration-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 md:p-6">
                  {getCurrentProducts().map((product) => (
                    <div
                      key={product.id}
                      className="flex flex-col items-center text-center space-y-3 p-4 bg-background rounded-lg"
                    >
                      {/* Product Image */}
                      <div className="w-full h-32 md:h-40 flex items-center justify-center">
                        <img
                          src={product.image}
                          alt={product.alt}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>

                      {/* Product Info */}
                      <div className="space-y-1 w-full">
                        <h3 className="font-heading font-semibold text-sm md:text-base text-textPrimary line-clamp-2 break-words">
                          {product.name}
                        </h3>
                        <p className="text-xs md:text-sm text-textSecondary break-words">
                          <span className="font-medium">Strength:</span> {product.strength}
                        </p>
                        <p className="text-xs md:text-sm text-textSecondary break-words">
                          <span className="font-medium">Form:</span> {product.dosageForm}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-card/90 hover:bg-card text-textPrimary p-2 rounded-full shadow-lg transition-all z-10"
              aria-label="Previous slide"
            >
              <Icon name="ChevronLeftIcon" size={20} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-card/90 hover:bg-card text-textPrimary p-2 rounded-full shadow-lg transition-all z-10"
              aria-label="Next slide"
            >
              <Icon name="ChevronRightIcon" size={20} />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide
                      ? 'bg-primary w-6' : 'bg-muted hover:bg-muted/70'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Auto-play Indicator */}
            <div className="absolute top-3 right-3 z-10">
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="bg-card/90 hover:bg-card text-textPrimary p-2 rounded-full shadow-lg transition-all"
                aria-label={isAutoPlaying ? 'Pause slideshow' : 'Play slideshow'}
              >
                {isAutoPlaying ? (
                  <Icon name="PauseIcon" size={16} />
                ) : (
                  <Icon name="PlayIcon" size={16} />
                )}
              </button>
            </div>
          </div>

          {/* View All Products Link */}
          <div className="text-center mt-6">
            <a
              href="/product-trading-portal"
              className="inline-flex items-center px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:shadow-lg transition-all font-semibold text-sm"
            >
              View All Products
              <Icon name="ArrowRightIcon" size={16} className="ml-2" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductSlideshow;